/** Media Session helpers (lock screen / Control Center / headphones). All calls are guarded. */

export type SessionAction = 'play' | 'pause' | 'seekbackward' | 'seekforward' | 'seekto' | 'previoustrack' | 'nexttrack';

const ACTIONS: SessionAction[] = ['play', 'pause', 'seekbackward', 'seekforward', 'seekto', 'previoustrack', 'nexttrack'];

function session(): MediaSession | null {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator ? navigator.mediaSession : null;
}

export function setSessionMetadata(meta: { title: string; artist: string; album?: string } | null): void {
  const s = session();
  if (!s) return;
  try {
    s.metadata =
      meta && typeof MediaMetadata !== 'undefined'
        ? new MediaMetadata({
            title: meta.title,
            artist: meta.artist,
            album: meta.album ?? '',
            artwork: [{ src: '/icons/icon-512.png', sizes: '512x512', type: 'image/png' }],
          })
        : null;
  } catch {
    /* unsupported */
  }
}

export function setSessionHandlers(handlers: Partial<Record<SessionAction, MediaSessionActionHandler>> | null): void {
  const s = session();
  if (!s) return;
  for (const a of ACTIONS) {
    try {
      s.setActionHandler(a, handlers?.[a] ?? null);
    } catch {
      /* action not supported by this browser */
    }
  }
}

export function setSessionPlaybackState(state: MediaSessionPlaybackState): void {
  const s = session();
  if (!s) return;
  try {
    s.playbackState = state;
  } catch {
    /* ignore */
  }
}

export function setSessionPosition(duration: number, position: number, rate: number): void {
  const s = session();
  if (!s || typeof s.setPositionState !== 'function') return;
  try {
    if (!Number.isFinite(duration) || duration <= 0) {
      s.setPositionState();
      return;
    }
    s.setPositionState({ duration, position: Math.min(duration, Math.max(0, position)), playbackRate: rate > 0 ? rate : 1 });
  } catch {
    /* ignore invalid state */
  }
}
